import React, { useState, useEffect } from 'react';
import partyData from '../data/partychecker.json';
import ActionItems from './ActionItems';

const RepresentativeSection = ({ selectedSchool }) => {
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [representative, setRepresentative] = useState(null);
  const [error, setError] = useState(null);
  const [callStartTime, setCallStartTime] = useState(null);

  const trackCallEvent = (rep) => {
    window.gtag && window.gtag('event', 'call_button_click', {
      event_category: 'Representative',
      event_label: rep.formalName,
      value: 1,
      party: rep.party,
      district: rep.district,
      has_school: Boolean(selectedSchool),
      zip_code: zip
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      const response = await fetch(
        `https://rws.capitol.texas.gov/api/MatchAddress?Address=${encodeURIComponent(address)}&City=${encodeURIComponent(city)}&Zip=${encodeURIComponent(zip)}&DistType=A`
      );
      
      if (!response.ok) throw new Error('Failed to fetch representative data');
      
      const data = await response.json();
      const houseRep = data.House?.[0]?.Member;
      const district = data.House?.[0]?.District;
      
      if (!houseRep) throw new Error('No representative found for this address');
      
      const partyInfo = partyData.find(d => d.district === district);
      if (!partyInfo) throw new Error('Unable to determine representative party');
      
      setRepresentative({
        name: `${houseRep.firstName} ${houseRep.lastName}`,
        phone: `(512) ${houseRep.capitolPhone.slice(0,3)}-${houseRep.capitolPhone.slice(3)}`,
        formalName: houseRep.formalName.replace('Representative ', ''),
        lastName: houseRep.lastName,
        party: partyInfo.party,
        district: district
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setRepresentative(null);
    setAddress('');
    setCity('');
    setZip('');
    setError(null);
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && callStartTime) {
        const callDuration = Date.now() - callStartTime;
        window.gtag('event', 'call_duration', {
          duration: callDuration,
          representative: representative?.formalName,
          party: representative?.party
        });
        setCallStartTime(null);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [callStartTime, representative]);

  if (representative) {
    return (
      <div className="representative-form">
        {representative.party === 'D' ? (
          <>
            <h2>You're represented by a Democrat.</h2>
            <p>Democrats are united in standing against the voucher scam. <strong>Call your representative to thank them for standing against vouchers:</strong></p>
          </>
        ) : (
          <>
            <h2>You're represented by Republican {representative.formalName}.</h2>
            <p>Call their office and ask them to stand against the voucher scam!</p>
          </>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <a
            href={`tel:${representative.phone}`}
            className="tel-button"
            onClick={() => trackCallEvent(representative)}
            onMouseDown={() => trackCallEvent({...representative, status: 'attempt'})}
          >
            Call Rep. {representative.formalName}
          </a>
          <ActionItems position="button" />
        </div>
        
        <div className="script-container">
          <h3>Need help calling? Try this script:</h3>
          {representative.party === 'D' ? (
            <p style={{ textAlign: 'left' }}>
              Hello, my name is [Your Name] and I'm a constituent in Rep. {representative.lastName}'s district living in zip code {zip}.<br /><br />I'm calling to thank the representative for their principled stand against the dangerous school voucher scam, SB 2. <br /><br />Please tell the representative that I appreciate their leadership on this issue and their understanding that Texas families need more resources for our public schools, not an unaccountable voucher scheme for private schools.<br /><br />I will continue to watch this legislation closely and implore the representative to maintain their principled stand against the voucher scam. Thank you for your time.
            </p>
          ) : (
            selectedSchool ? (
              <p style={{ textAlign: 'left' }}>
                Hello, my name is [Your Name] and I'm a constituent in Rep. {representative.lastName}'s district living in zip code {zip}.<br /><br />
                I'm calling to express my strong opposition to the school voucher scam, SB 2, and ask them to vote against it.<br /><br />
                Local schools in {selectedSchool.district} stand to lose as much as $
                {Number(selectedSchool['5pt']).toLocaleString()} under SB 2, which would be a devastating loss for our community.<br /><br />
                Public funds should stay in our public schools, not be divvied up to unaccountable private schools.
              </p>
            ) : (
              <p style={{ textAlign: 'left' }}>
                Hello, my name is [Your Name] and I'm a constituent in Rep. {representative.lastName}'s district living in zip code {zip}.<br /><br />
                I'm calling to express my strong opposition to the school voucher scheme, SB 2, and ask them to vote against it.<br /><br />
                Public funds should stay in our public schools, not be divvied up to unaccountable private schools. Do not sell out our kids for billionaire donors — Texas families are watching, and so are your own children and grandchildren.<br /><br />
                I implore the representative to vote NO on school vouchers and stand for strong public schools, not corporate interests. Thank you for your time.
              </p>
            )
          )}
        </div>
        
        <p 
          onClick={resetForm} 
          className="representative-link"
          style={{ cursor: 'pointer', textDecoration: 'underline', marginTop: '2rem' }}
        >
          Check Another Address
        </p>
        
        <p style={{ fontStyle: 'italic', marginTop: '2rem' }}>
          <a href="https://wrm.capitol.texas.gov/" target="_blank" rel="noopener noreferrer" className="verification-link">
            Click here
          </a> to verify your representative.
        </p>
      </div>
    );
  }

  return (
    <div className="representative-form">
      <h1 className="impact-header">It's going to take <span style={{ color: '#4a90e2' }}>all of us</span> to defeat the voucher&nbsp;scam.<br /></h1>
      <h2 className="subheader">Enter your information to find your representative and ask them to stand up for Texas local schools:</h2>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Street Address"
          className="representative-input"
        />
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="City"
          className="representative-input"
        />
        <input
          type="text"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          placeholder="ZIP Code *"
          className="representative-input"
        />
        <button 
          type="submit" 
          className="representative-button"
          disabled={!zip}
        >
          Find Representative
        </button>
      </form>
      {error && <p style={{ color: 'var(--accent-color)', marginTop: '1rem' }}>{error}</p>}
    </div>
  );
};

export default RepresentativeSection; 